<script lang="ts">
  import { onMount } from "svelte";
  import { storyblokEditable } from "@storyblok/svelte";
  import AutoplayVideo from "./AutoplayVideo.svelte";
  import ArrowButton from "./ArrowButton.svelte";
  import CountUpStatItem from "$lib/molecules/CountUpStatItem.svelte";

  // Get the block data
  export let blok;

  // Define the delivery stats data
  interface DeliveryStats {
    total_distance_flown_mi: number;
    num_deliveries: number;
    num_products_delivered: number;
  }

  // Fetch the delivery stats
  let deliveryStats = {} as DeliveryStats;
  let deliveryStatsLoaded = false;

  async function getStats() {
    try {
      const response = await fetch(
        "https://delivery-counter.platform.flyzipline.com/api/v1/public/stats/",
      );
      deliveryStats = await response.json();
      deliveryStatsLoaded = true;
    } catch (error) {
      console.error("Error fetching delivery stats:", error);
    }
  }

  onMount(() => {
    getStats();
  });
</script>

<section class="stats-video-section section-header-width mx-auto" use:storyblokEditable={blok}>
  <div class="text-wrapper">
    <p class="eyebrow">{blok.eyebrow}</p>
    <h1 class="main-heading">{blok.heading}</h1>
    <h2 class="main-subheading">{blok.subheading}</h2>
    <ArrowButton
      buttonText={blok.buttonText}
      buttonLink={blok.buttonLink}
      textColor="#000000"
      buttonColor="#ffffff"
    />
  </div>
  <div class="video-wrapper">
    {#if deliveryStatsLoaded}
      <div class="stats">
        <div class="stat">
          <div class="stat-number">
            <CountUpStatItem value={deliveryStats.total_distance_flown_mi} />
          </div>
          <div class="stat-text">miles flown</div>
        </div>

        <div class="stat">
          <div class="stat-number">
            <CountUpStatItem value={deliveryStats.num_deliveries} />
          </div>
          <div class="stat-text">deliveries made</div>
        </div>

        <div class="stat">
          <div class="stat-number">
            <CountUpStatItem value={deliveryStats.num_products_delivered} />
          </div>

          <div class="stat-text">items delivered</div>
        </div>
      </div>
    {/if}

    <AutoplayVideo
      videoMobile={blok.videoMobile}
      videoDesktop={blok.videoDesktop}
      videoPlaceholderMobile={blok.videoPlaceholder}
      videoPlaceholderDesktop={blok.videoPlaceholder}
    />
  </div>
</section>

<style lang="postcss">
  .stats-video-section {
    margin-top: 4rem;
    margin-bottom: 4rem;

    @screen lg {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-areas: "video text";
      gap: 8rem;
      margin-top: 8rem;
      margin-bottom: 8rem;
      align-items: center;
    }
  }

  .video-wrapper {
    position: relative;
    border-radius: 10px;
    overflow: hidden;
    grid-area: video;
  }

  .stats {
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 90%;
    transform: translateX(-50%);
    z-index: 10;
  }

  .stat {
    margin-bottom: 2rem;
    padding-top: 0.25rem;
    display: flex;
    justify-content: space-between;
    border-top: 1px solid #fff;
    color: #fff;

    &:last-child {
      margin-bottom: 1rem;

      @screen lg {
        margin-bottom: 2rem;
      }
    }
  }

  .stat-number {
    font-weight: var(--font-weight-medium);

    @screen lg {
      font-size: 24px;
    }
  }

  .stat-text {
    font-weight: var(--font-weight-light);

    @screen lg {
      font-size: 18px;
    }
  }

  .text-wrapper {
    margin-bottom: 3rem;
    grid-area: text;
  }

  .eyebrow {
    font-size: 12px;
    font-weight: var(--font-weight-bold);
    line-height: 110%;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #fff;

    @screen lg {
      font-size: 16px;
    }
  }

  .main-heading {
    margin-top: 1.25rem;
    max-width: 400px;
    font-size: 38px;
    font-family: var(--font-secondary);
    font-weight: var(--font-weight-semibold);
    line-height: 1.1;
    color: #fff;
    white-space: pre-wrap;

    @screen md {
      font-size: 50px;
    }

    @screen lg {
      font-size: 64px;
    }
  }

  .main-subheading {
    margin-top: 1.5rem;
    margin-bottom: 2rem;
    max-width: 450px;
    font-size: 16px;
    font-weight: 300;
    line-height: 1.6;
    color: #fff;
    white-space: pre-wrap;

    @screen lg {
      font-size: 18px;
    }
  }
</style>
